import { Component, Input } from '@angular/core'
import { Dialog } from '@angular/cdk/dialog'
import { closeJobSelectionDialog } from '../../../shared/utils/job-selection-dialog-utils'
import {
    PipelineType,
    SegmentationType,
    CustomSegmentationParameters,
} from '../../../shared/model/jobs'
import { JobApiService } from '../../../shared/services/job-api.service'
import { Observable } from 'rxjs'
import { StaticLayerConfig } from '../../../state/viewer/viewer.state'
import { Store } from '@ngrx/store'
import { selectImageLayers } from '../../../state/viewer/viewer.selectors'
import { ToasterService } from '../../../shared/services/toaster.service'
import { HttpErrorResponse } from '@angular/common/http'
import { environment } from '../../../../environments/environment'
import {
    LayerListDialogComponent,
    LayerListDialogData,
} from './layer-list-dialog/layer-list-dialog.component'

/** Task option: predefined type or "Other" for custom string. */
const CUSTOM_SEGMENTATION_TASK_OPTIONS: { value: SegmentationType | 'other'; label: string }[] = [
    ...(Object.entries(SegmentationType) as [string, SegmentationType][])
        .filter(([, value]) => typeof value === 'string')
        .map(([label, value]) => ({ value, label })),
    { value: 'other', label: 'Other (specify)' },
]

@Component({
    selector: 'app-custom-segmentation-selection',
    templateUrl: './custom-segmentation-selection.component.html',
    styleUrls: ['./custom-segmentation-selection.component.scss'],
})
export class CustomSegmentationSelectionComponent {
    @Input()
    siblingDatasets: string[] | never[]

    imageLayers$: Observable<StaticLayerConfig[]>

    readonly taskOptions = CUSTOM_SEGMENTATION_TASK_OPTIONS
    readonly SegmentationType = SegmentationType
    /** Selected task: predefined type or 'other'. When 'other', use customTaskName. */
    selectedTask: SegmentationType | 'other' | null = null
    /** Custom task name when selectedTask === 'other'. */
    customTaskName = ''

    /** For task Cell: membrane layers. */
    selectedMembraneLayers: StaticLayerConfig[] = []
    /** For task Cell: optional nucleus layers. */
    selectedNucleusLayers: StaticLayerConfig[] = []
    /** For non-Cell tasks (Nucleus, Tissue, etc.): input channel layers. */
    selectedChannelLayers: StaticLayerConfig[] = []

    /** Optional description / notes from the client. */
    description = ''

    submitted = false
    runBatchJob = false

    private readonly wikiUrl = environment.wikiUrl

    constructor(
        store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService,
        private readonly dialog: Dialog
    ) {
        this.imageLayers$ = store.select(selectImageLayers)
    }

    get isCellTask(): boolean {
        return this.selectedTask === SegmentationType.Cell
    }

    get isOtherTask(): boolean {
        return this.selectedTask === 'other'
    }

    get canStart(): boolean {
        if (this.submitted || this.selectedTask == null) return false
        if (this.selectedTask === 'other' && !this.customTaskName.trim()) return false
        if (this.isCellTask) return this.selectedMembraneLayers.length >= 1
        return this.selectedChannelLayers.length >= 1
    }

    onTaskChange(): void {
        this.selectedMembraneLayers = []
        this.selectedNucleusLayers = []
        this.selectedChannelLayers = []
    }

    openLayerListDialog(
        section: 'membrane' | 'nucleus' | 'channel',
        title: string
    ): void {
        const current =
            section === 'membrane'
                ? this.selectedMembraneLayers
                : section === 'nucleus'
                ? this.selectedNucleusLayers
                : this.selectedChannelLayers
        const data: LayerListDialogData = {
            title,
            availableLayers$: this.imageLayers$,
            currentSelection: [...current],
        }
        const dialogRef = this.dialog.open<StaticLayerConfig[] | null>(
            LayerListDialogComponent,
            { data, width: '400px' }
        )
        dialogRef.closed.subscribe(result => {
            if (result != null) {
                if (section === 'membrane') this.selectedMembraneLayers = result
                else if (section === 'nucleus') this.selectedNucleusLayers = result
                else this.selectedChannelLayers = result
            }
        })
    }

    getLayersSummary(layers: StaticLayerConfig[]): string {
        if (layers.length === 0) return 'No layers selected'
        return layers.map(l => l.name ?? '').filter(Boolean).join(', ')
    }

    cancel(): void {
        closeJobSelectionDialog()
    }

    startJob(): void {
        if (!this.canStart || this.selectedTask == null) return
        if (this.selectedTask === 'other' && !this.customTaskName.trim()) return
        this.submitted = true

        const taskValue =
            this.selectedTask === 'other'
                ? this.customTaskName.trim()
                : this.selectedTask

        const parameters: CustomSegmentationParameters = {
            type: PipelineType.Segmentation,
            task: taskValue,
            batch: this.runBatchJob,
            description: (this.description ?? '').trim(),
        }

        if (this.isCellTask) {
            parameters.membrane = this.selectedMembraneLayers.map(l => l.name!).filter(Boolean)
            if (this.selectedNucleusLayers.length > 0) {
                parameters.nucleus = this.selectedNucleusLayers.map(l => l.name!).filter(Boolean)
            }
        } else {
            parameters.channel_layers = this.selectedChannelLayers.map(l => l.name!).filter(Boolean)
        }

        this.jobApi.startSegmentationJob(parameters).subscribe({
            next: () => closeJobSelectionDialog(),
            error: (err: HttpErrorResponse) => {
                this.toaster.httpError(err, 'Error starting job')
                this.submitted = false
            },
        })
    }

    goToWiki(): void {
        window.open(`${this.wikiUrl}home/Working_with_jobs#h-2-cell-segmentation`, '_blank')
    }
}
